import { Component, Input, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { isNumeric, roundTo } from '@assetaccountant/core/utils/numeric.utils';

@Component({
  selector: 'app-percentage-input',
  templateUrl: './percentage-input.component.html',
  standalone: false,
})
export class PercentageInputComponent implements ControlValueAccessor {
  model: number | null = null;

  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() placeholder = '';
  @Input() readOnly = false;
  @Input() size: 'sm' | 'lg' | undefined;

  /**
   * How many decimal places in result returned
   * e.g. precision = 6 means: 33.3333% OR 0.333333
   */
  @Input() precision = 6;

  isDisabled = false;

  private _onChange?: (val: number | null) => void;
  private _onTouched?: () => void;

  get showError() {
    return !!(this.control && this.control.invalid && this.control.touched);
  }

  constructor(@Self() @Optional() private control?: NgControl) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  writeValue(value: number | string | null): void {
    this.model = isNumeric(value) ? this.getRoundedPercentage(+value * 100) : null;
  }

  onChange(val: string | number | null) {
    if (isNumeric(val)) {
      this._onChange?.(this.getRoundedRate(+val / 100));
    } else {
      this._onChange?.(null);
    }
  }

  onBlur() {
    if (isNumeric(this.model)) {
      const roundedModel = this.getRoundedPercentage(this.model);
      if (this.model !== roundedModel) {
        this.model = this.getRoundedPercentage(this.model);
        this.onChange?.(this.model);
      }
    }
    this._onTouched?.();
  }

  private getRoundedPercentage = (percentage: number) => roundTo(percentage, this.precision - 2);
  private getRoundedRate = (rate: number) => roundTo(rate, this.precision);

  registerOnChange(fn: (val: number | null) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
